import React from 'react';
import { noop } from 'lodash';
import {
  CbObjOnFieldValid as FieldValid,
  CbObjOnFocus as CardFocus,
  CbObjOnBrand as CardBrand,
  CbObjOnBinValue as CardBinValue,
} from '@adyen/adyen-web/dist/types/components/internal/SecuredFields/lib/types';
import { AdyenCardType } from './adyen';
import { useFiveDigitLocale } from './user_five_digit_locale_hook';
import { AdyenCheckout, CustomCard } from '@adyen/adyen-web-v6';
import type { AdyenCheckoutError, CoreConfiguration, OnChangeData, UIElement, ValidationError } from '@adyen/adyen-web-v6';
import { useTheme } from '@reverbdotcom/cadence/components';
import { useUser } from './user_hooks';

// https://docs.adyen.com/payment-methods/cards/custom-card-integration#styling
const getStyles = (darkMode: boolean) => {
  const color = darkMode ? '#f5f5f5' : '#212121'; // rc-color-text-primary

  return {
    base: {
      background: 'transparent',
      color,
      fontSize: '16px',
      fontSmoothing: 'auto',
      // eslint-disable-next-line max-len
      fontFamily: 'Consolas, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", Monaco, "Courier New", Courier, monospace',
      fontWeight: '400',
      lineHeight: '1.4',
      padding: '6px 8px',
    },
    placeholder: {
      color: darkMode ? '#9e9e9e' : '#757575', // rc-color-text-tertiary
    },
    error: {
      color,
    },
  };
};

// CustomCard in Adyen Web SDK v6 behaves a bit differently than before re: "brands" field.
//
// When we render only a CVV, or Expiry + CVV (e.g. CVV-only for a saved/vaulted card, or updating a known card)
// -- and a card number field is not present --
// a brands array with only one brand (the known brand) is the only way to configure this without getting an error from Adyen,
// or a failure by Adyen to use the correct CVV length (Amex cards use 4-digit CVV).
//
// All other card forms need the full list of brands so that the card number field can detect the brand.
const ALL_BRANDS = ['amex', 'cup', 'diners', 'discover', 'jcb', 'mc', 'visa'];

function getBrands(cardType: AdyenCardType) {
  if (!cardType || cardType === AdyenCardType.Card) return ALL_BRANDS;

  return [cardType];
}

type BrandCallback = (brand: string) => void;
type CardChangeCallback = (state: OnChangeData, component: UIElement) => void;
type EndDigitsCallback = (endDigits: string) => void;

// NEW behavior in Adyen web SDK v6!
// onError is now only called in two different scenarios:
// - when a critical error happened (network error; implementation error; script failed to load)
// - when the shopper cancels the payment flow in payment methods that have an overlay (GooglePay, PayPal, ApplePay)
//
// For individual field validation errors, use onValidationError
type ErrorCallback = (error: AdyenCheckoutError, component?: UIElement) => void;

// NEW behavior in Adyen web SDK v6!
// Called as errors are detected within the individual securedFields.
//
// Adyen hands us an array of the invalid fields.
// We key it by fieldType for consumers, which look errors up as `errors[cseName]?.errorI18n`.
type ValidationErrors = Record<string, ValidationError>;
type ValidationErrorCallback = (validationErrors: ValidationErrors) => void;

type BinValueCallback = (binValue: string) => void;

export interface AdyenCardOptionsV6 {
  selector: string;
  cardType: AdyenCardType;
  onBrand?: BrandCallback;
  onCardChange?: CardChangeCallback;
  onEndDigits?: EndDigitsCallback;
  onError?: ErrorCallback;
  onBinValue?: BinValueCallback;
  onValidationError?: ValidationErrorCallback;
  enabled?: boolean; // when false, skip mounting the Adyen component
  environment?: string;
  clientKey?: string;
}

export interface AdyenCardResponseV6 {
  focus: Partial<CardFocus>;
  errors: ValidationErrors;
  cardState: OnChangeData;
  endDigits: string;
  brand: string;
  binValue: string;
}

// Shared custom credit card hook (https://docs.adyen.com/payment-methods/cards/custom-card-integration)
// Documentation about onValidationError in v6 SDK upgrade: https://docs.adyen.com/online-payments/upgrade-your-integration/upgrade-to-web-v6/from-v4#upgrade-configuration
export function useAdyenCardV6(options: AdyenCardOptionsV6): AdyenCardResponseV6 {
  const locale = useFiveDigitLocale();
  const user = useUser();
  const countryCode = user.countryCode || 'US';
  const { darkMode = false } = useTheme();
  const { environment, clientKey } = options;

  const { cardType, selector } = options;
  const [validationErrors, setValidationErrors] = React.useState<ValidationErrors>({});
  const [cardState, setCardState] = React.useState<OnChangeData>({
    isValid: false,
    data: {
      paymentMethod: { type: 'card' },
      clientStateDataIndicator: false,
    },
  });

  const [focus, setFocus] = React.useState({});
  const [endDigits, setEndDigits] = React.useState('');
  const [brand, setBrand] = React.useState('');
  const [binValue, setBinValue] = React.useState('');

  React.useEffect(() => {
    if (options.enabled === false || !environment || !clientKey) {
      return;
    }
    const onBrand = buildBrandCallback(setBrand, options.onBrand || noop);
    const onChange = buildCardChangeCallback(setCardState, options.onCardChange || noop);
    const onValidationError = buildValidationErrorCallback(setValidationErrors, options.onValidationError || noop);

    const onFieldValid = buildFieldValidCallback({
      setEndDigits,
      onEndDigits: options.onEndDigits || noop,
    });

    const onBinValue = buildBinValidCallback(setBinValue, options.onBinValue || noop);

    const initializeCard = async () => {
      const checkout = await AdyenCheckout({
        clientKey,
        environment: environment as CoreConfiguration['environment'],
        locale,
        countryCode,
      });

      const styles = getStyles(darkMode);

      return new CustomCard(checkout, {
        brands: getBrands(cardType),
        styles,
        onChange,
        onFocus: onFocus(setFocus),
        onBrand,
        onFieldValid,
        onBinValue,
        onValidationError,
      }).mount(selector);
    };

    initializeCard();
  }, [
    locale,
    selector,
    environment,
    clientKey,
    cardType,
    setCardState,
    setFocus,
    setValidationErrors,
    setEndDigits,
    setBinValue,
    darkMode,
    options.enabled,
    options.onCardChange,
    options.onEndDigits,
    options.onError,
    options.onBrand,
    options.onBinValue,
  ]);

  return { focus, errors: validationErrors, cardState, endDigits, brand, binValue };
}

function onFocus(setFocus: React.Dispatch<React.SetStateAction<Partial<CardFocus>>>) {
  return (event: CardFocus) => {
    setFocus(event);
  };
}

// This cb functon SETS and CLEARS validation errors.
//
// v6 passes every field that has ever had errors, not just the currently-invalid ones.
// once a field becomes valid Adyen re-sends it with an empty "error" field, rather than dropping it.
// So we need to ".filter" to clear the resolved errors.
//
// For example:
// [
//   { fieldType: 'encryptedCardNumber', error: '', rootNode: … },        // valid now. Gets filtered out/CLEARED
//   { fieldType: 'encryptedSecurityCode', error: 'error.va.sf-cc-cvc.01', errorI18n: 'Incomplete field', … }, // invalid
// ]
function buildValidationErrorCallback(
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>,
  onValidationError: ValidationErrorCallback,
) {
  return (validationErrors: ValidationError[]) => {
    const errors: ValidationErrors = Object.fromEntries(
      validationErrors.filter((e) => e.error).map((e) => [e.fieldType, e]),
    );
    onValidationError(errors);
    setValidationErrors(errors);
  };
}


function buildCardChangeCallback(setCardState: React.Dispatch<React.SetStateAction<OnChangeData>>, onCardChange: CardChangeCallback) {
  return (state: OnChangeData, component: UIElement) => {
    onCardChange(state, component);
    setCardState(state);
  };
}


function buildBrandCallback(setBrand: React.Dispatch<React.SetStateAction<string>>, onBrand: BrandCallback) {
  return (event: CardBrand) => {
    onBrand(event.brand);
    setBrand(event.brand);
  };
}

function buildFieldValidCallback({
  setEndDigits,
  onEndDigits,
}: {
  setEndDigits: React.Dispatch<React.SetStateAction<string>>;
  onEndDigits: EndDigitsCallback;
}) {
  return (event: FieldValid) => {
    if (event.fieldType === 'encryptedCardNumber' && event.endDigits) {
      onEndDigits(event.endDigits);
      setEndDigits(event.endDigits);
    }
  };
}

function buildBinValidCallback(setBinValue: React.Dispatch<React.SetStateAction<string>>, onBinValue: BinValueCallback) {
  return (event: CardBinValue) => {
    onBinValue(event.binValue);
    setBinValue(event.binValue);
  };
}

export const callbacks = {
  onBrand: buildBrandCallback,
  onChange: buildCardChangeCallback,
  onValidationError: buildValidationErrorCallback,
  onFieldValid: buildFieldValidCallback,
  onBinValue: buildBinValidCallback,
  onFocus,
};
